<template>
  <div class="receipt receipt--view">
    <vue-headful :title="`Receipt - ${receiptStreetAddress}`"/>

    <ReceiptLoader v-if="apiLoading" />
    <template v-if="apiLoaded">
      <div class="inspection-pg">
        <AddressTitleBar
          :hg-address="receiptStreetAddress"
          :hg-location="receiptLocation"
          :hg-logo="company.logo"
          class="title">
          <InspectionButton :hg-folder-id="folderId"/>
        </AddressTitleBar>
      </div>

      <v-layout v-if="showPaymentCompleted">
        <v-flex
          xs10
          offset-xs1
          offset-sm4
          sm5
          class="header">
          <Alert
            v-if="showPaymentCompleted"
            type="success"
            @close="showPaymentCompleted = false">
            Your payment was successful.
          </Alert>
        </v-flex>
      </v-layout>

      <PaymentReceivedHeader :receipt="receipt" />

      <v-container>
        <v-layout row wrap>
          <v-flex xs12 md10 offset-md1>
            <Receipt
:hg-company="company"
:hg-receipt="receipt"
:refund-amount="formattedRefundAmount"
:refund-date="formattedRefundDate"
:has-refund="hasRefund" />
            <PaymentFooter :receipt="receipt" />
          </v-flex>
        </v-layout>
      </v-container>
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex';

import Alert from '@/components/Shared/Alert.vue';
import BaseViewComponent from '@/components/Shared/BaseViewComponent.vue';
import ReceiptLoader from '@/components/Loaders/ReceiptLoader.vue';
import AddressTitleBar from '../AddressBar/AddressTitleBar.vue';
import InspectionButton from '../AddressBar/InspectionButton.vue';
import Receipt from '../Payments/Receipt.vue';
import PaymentReceivedHeader from '../Payments/Receipt/PaymentReceivedHeader.vue';
import PaymentFooter from '../Payments/Receipt/PaymentFooter.vue';

export default {
  name: 'hgReceipt',
  extends: BaseViewComponent,
  components: {
    AddressTitleBar,
    Alert,
    InspectionButton,
    PaymentFooter,
    PaymentReceivedHeader,
    Receipt,
    ReceiptLoader,
  },
  mounted() {
    this.initialHydration([
      this.$store.dispatch('Receipt/loadReceipt', {
        folderId: this.folderId,
        paymentId: this.paymentId,
      }),
      this.$store.dispatch('Company/loadCompany', this.companyId),
    ]);
  },
  props: {
    companyId: {
      type: String,
      required: true,
    },
    folderId: {
      type: String,
      required: true,
    },
    paymentComplete: {
      type: Boolean,
      default: false,
    },
    paymentId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      showPaymentCompleted: this.paymentComplete,
      hasDispatched: false,
    };
  },
  watch: {
    isReadyToGetCompanySetting: {
      handler(isReady) {
        if (isReady && !this.hasDispatched) {
          this.$store.dispatch('Company/loadCompanyAddtionalChargesSetting', { companyId: this.company.companyId, settingKey: 'TECHNOLOGY_FEE' });
          this.hasDispatched = true;
        }
      },
      inmmediate: true,
    },
  },
  computed: {
    ...mapState('Company', {
      company: state => state.company,
    }),
    ...mapState('Receipt', {
      receipt: state => state.receipt,
    }),
    isReadyToGetCompanySetting() {
      return this.receipt && this.company;
    },
    hasRefund() {
      return this.receipt && this.receipt.amountRefunded && this.receipt.amountRefunded > 0;
    },
    formattedRefundAmount() {
      const amount = this.receipt?.amountRefunded || 0;

      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(amount);
    },
    formattedRefundDate() {
      const dateString = this.receipt?.refundedDateTime;

      if (!dateString) return '';

      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    receiptStreetAddress() {
      if ((!this.receipt || !this.receipt.folder)
      || (!this.receipt.folder.address1 && !this.receipt.folder.address2)) {
        return '';
      }
      if (this.receipt.folder.address1 && this.receipt.folder.address2) {
        return `${this.receipt.folder.address1}, ${this.receipt.folder.address2}`;
      }
      return (this.receipt.folder.address1 ? `${this.receipt.folder.address1}` : `${this.receipt.folder.address2}`);
    },
    receiptLocation() {
      if (!this.receipt || !this.receipt.folder) {
        return '';
      }
      if (!this.receipt.folder.city) {
        return `${this.receipt.folder.state} ${this.receipt.folder.postalCode}`;
      }
      if (!this.receipt.folder.state && !this.receipt.folder.postalCode) {
        return this.receipt.folder.city;
      }
      return `${this.receipt.folder.city}, ${this.receipt.folder.state} ${this.receipt.folder.postalCode}` || '';
    },
  },
  beforeRouteUpdate(to, from, next) {
    if (!to.hash) { /* ignore anchor tags for scrolling */
      this.created();
      next();
    }
  },
};
</script>

<style lang="scss" scoped>
@import '../../styles/vars/theme';

.header {
  padding-top: 40px;
  text-align: center;
}

.receipt {
  color: lighten($theme-color-gray, 19.22);
  font-size: 1.214rem;
  padding-bottom: 20px;
}
</style>
